import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  IoMailOutline,
  IoCallOutline,
  IoTimeOutline,
  IoLocationOutline,
} from "react-icons/io5";
import { FaFacebookF, FaInstagram } from "react-icons/fa";
import { useLanguage } from "../contexts/LanguageContext";
import { marketingI18n } from "../content/homeMarketing";
import useScrollAnimate from "../hooks/useScrollAnimate";
import MarketingPageIntro from "./marketing/MarketingPageIntro";
import "./Home.css";
import "./MarketingPages.css";
import "./Contact.css";

const SUPPORT_EMAIL = "bookwithehgezly@gmail.com";
const SUPPORT_PHONE = "+201116640224";

const SOCIAL_LINKS = [
  {
    Icon: FaFacebookF,
    url: "https://www.facebook.com/share/184zUxhmEW/?mibextid=wwXIfr",
    label: "Facebook",
  },
  {
    Icon: FaInstagram,
    url: "https://www.instagram.com/bookwithehgezly?igsh=M3kzOTh1YjRmdmli",
    label: "Instagram",
  },
];

const emptyForm = { name: "", email: "", topic: "booking", message: "" };

export default function Contact() {
  const { lang } = useLanguage();
  const pageT = marketingI18n[lang].pages.contact;
  useScrollAnimate([lang]);
  const [form, setForm] = useState(emptyForm);

  const channels = [
    {
      id: "email",
      href: `mailto:${SUPPORT_EMAIL}`,
      icon: IoMailOutline,
      label: pageT.emailLabel,
      value: pageT.emailValue,
      hint: pageT.emailHint,
    },
    {
      id: "phone",
      href: `tel:${SUPPORT_PHONE}`,
      icon: IoCallOutline,
      label: pageT.phoneLabel,
      value: pageT.phoneValue,
      hint: pageT.phoneHint,
    },
    {
      id: "hours",
      href: "#contact-form",
      icon: IoTimeOutline,
      label: pageT.hoursLabel,
      value: pageT.hoursValue,
      hint: pageT.hoursHint,
    },
  ];

  const update = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const topic =
      pageT.topics.find((item) => item.value === form.topic)?.label || form.topic;
    const subject = `[Ehgezly] ${topic}: ${form.name || "Contact"}`;
    const body = [
      form.message.trim(),
      "",
      `${form.name}, ${form.email}`,
      `Topic: ${topic}`,
    ].join("\n");
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div
      className={`marketing-page contact-page ${lang === "ar" ? "rtl" : ""}`}
      dir={lang === "ar" ? "rtl" : "ltr"}
    >
      <MarketingPageIntro variant="contact" page={pageT} lang={lang} layout="split" />

      <section className="contact-channels-section" aria-label={pageT.sectionsLabel}>
        <div className="container">
          <ul className="contact-channels-grid">
            {channels.map((item, idx) => {
              const Icon = item.icon;
              return (
                <li key={item.id}>
                  <a
                    href={item.href}
                    className={`contact-channel-card scroll-animate from-bottom delay-${Math.min(idx + 1, 4)}`}
                  >
                    <span className="contact-channel-card__icon" aria-hidden="true">
                      <Icon />
                    </span>
                    <p className="contact-channel-card__label">{item.label}</p>
                    <p className="contact-channel-card__value">{item.value}</p>
                    <p className="contact-channel-card__hint">{item.hint}</p>
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <section className="contact-form-section" id="contact-form" aria-labelledby="contact-form-heading">
        <div className="container contact-form-layout">
          <form className="contact-form scroll-animate from-bottom" onSubmit={handleSubmit}>
            <h2 id="contact-form-heading" className="contact-form__title">
              {pageT.formTitle}
            </h2>
            <p className="contact-form__lead">{pageT.formLead}</p>

            <label className="contact-field">
              <span>{pageT.nameLabel}</span>
              <input
                type="text"
                name="name"
                required
                autoComplete="name"
                placeholder={pageT.namePh}
                value={form.name}
                onChange={update("name")}
              />
            </label>

            <label className="contact-field">
              <span>{pageT.emailFieldLabel}</span>
              <input
                type="email"
                name="email"
                required
                autoComplete="email"
                placeholder={pageT.emailPh}
                value={form.email}
                onChange={update("email")}
              />
            </label>

            <label className="contact-field">
              <span>{pageT.topicLabel}</span>
              <select
                className="contact-field__select"
                name="topic"
                value={form.topic}
                onChange={update("topic")}
              >
                {pageT.topics.map((topic) => (
                  <option key={topic.value} value={topic.value}>
                    {topic.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="contact-field">
              <span>{pageT.messageLabel}</span>
              <textarea
                name="message"
                required
                rows={5}
                placeholder={pageT.messagePh}
                value={form.message}
                onChange={update("message")}
              />
            </label>

            <button type="submit" className="contact-form__submit">
              {pageT.submit}
            </button>
          </form>

          <aside className="contact-aside scroll-animate from-bottom delay-2">
            <div className="contact-aside__card">
              <h3 className="contact-aside__title">{pageT.nextTitle}</h3>
              <ol className="contact-aside__steps">
                {pageT.nextSteps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </div>

            <div className="contact-aside__card">
              <span className="contact-aside__icon" aria-hidden="true">
                <IoLocationOutline />
              </span>
              <h3 className="contact-aside__title">{pageT.basedIn}</h3>
              <p className="contact-aside__desc">{pageT.basedInDesc}</p>
              <p className="contact-aside__meta">{pageT.hoursExtra}</p>
            </div>

            <div className="contact-aside__card">
              <h3 className="contact-aside__title">{pageT.followTitle}</h3>
              <div className="contact-aside__socials">
                {SOCIAL_LINKS.map(({ Icon, url, label }) => (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-social-btn"
                    aria-label={label}
                  >
                    <Icon />
                  </a>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="contact-reasons-section" aria-labelledby="contact-reasons-heading">
        <div className="container">
          <h2 id="contact-reasons-heading" className="contact-reasons__title">
            {pageT.reasonsTitle}
          </h2>
          <ul className="contact-reasons-grid">
            {pageT.reasons.map((item, idx) => (
              <li
                key={item.title}
                className={`contact-reason-card scroll-animate from-bottom delay-${Math.min(idx + 1, 4)}`}
              >
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="contact-cta-section">
        <div className="container contact-cta-inner scroll-animate from-bottom delay-2">
          <h2 className="contact-cta-title">{pageT.ctaTitle}</h2>
          <p className="contact-cta-desc">{pageT.ctaDesc}</p>
          <div className="contact-cta-actions">
            <Link to={pageT.ctaSecondaryTo} className="contact-cta-btn contact-cta-btn--primary">
              {pageT.ctaSecondary}
            </Link>
            <Link to="/faq" className="contact-cta-btn contact-cta-btn--secondary">
              {pageT.ctaFaq}
            </Link>
          </div>
        </div>
      </section>

      <div className="section-wave section-wave--light" aria-hidden="true" />
    </div>
  );
}
